import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ExternalLink, AlertTriangle, ArrowRight, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { TAX_UPDATES, IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = {
  en: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
  ar: ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"],
};

export default function TaxUpdates() {
  const { t, isAr } = useLang();
  const [live, setLive] = useState(null);
  const Arrow = ({ className = "" }) => <ArrowRight className={`${className} ${isAr ? "rotate-180" : ""}`} />;

  useEffect(() => {
    axios.get(`${API}/tax-updates`).then((r) => setLive(r.data || [])).catch(() => setLive([]));
  }, []);

  const dateParts = (iso) => {
    const dt = new Date(iso);
    if (isNaN(dt)) return { d: "•", m: "", y: "" };
    return { d: String(dt.getDate()).padStart(2, "0"), m: MONTHS[isAr ? "ar" : "en"][dt.getMonth()], y: String(dt.getFullYear()) };
  };
  const useLive = live && live.length > 0;

  return (
    <div className="bg-ivory">
      <section className="relative overflow-hidden border-b border-line" data-testid="updates-hero">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url(${IMAGES.brandTexture})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-ivory/50 to-ivory" />
        <div className="container-tnsq relative py-20 lg:py-24">
          <Reveal>
            <div className="text-sm text-muted mb-5">
              <Link to="/" className="hover:text-navy">{t({ en: "Home", ar: "الرئيسية" })}</Link>
              <span className="mx-2">/</span>
              <span className="text-ink font-medium">{t({ en: "Tax Update Hub", ar: "مركز التحديثات" })}</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <span className="kicker gold-line">{t({ en: "2026 Regulatory Feed", ar: "التحديثات التشريعية 2026" })}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.1] max-w-4xl mt-4">
              {t({ en: "UAE Tax Update Hub", ar: "مركز تحديثات الضرائب في الإمارات" })}
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 text-muted text-base md:text-lg max-w-2xl leading-relaxed">
              {t({ en: "Selected tax and compliance developments that materially affect UAE businesses. Editorial review: 23 August 2026.", ar: "أهم التطورات الضريبية والامتثال التي تؤثر فعلياً على الأعمال في الإمارات. آخر مراجعة تحريرية: 23 أغسطس 2026." })}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="updates-list">
        <div className="container-tnsq max-w-4xl">
          <Reveal>
            <div className="flex gap-3 rounded-2xl border border-gold/40 bg-gold/[0.06] px-5 py-4 mb-12">
              <AlertTriangle className="h-5 w-5 text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-ink/70 leading-relaxed">
                {t({ en: "This page is a practical summary, not the legislation itself. Always check the official FTA / MoF source and the facts of your specific case before acting.", ar: "هذه الصفحة ملخص عملي وليست نص التشريع نفسه. يُرجى دائماً مراجعة مصدر الهيئة أو وزارة المالية الرسمي ووقائع حالتك قبل اتخاذ أي إجراء." })}
              </p>
            </div>
          </Reveal>

          <div className="space-y-6">
            {useLive ? live.map((u, i) => {
              const dp = dateParts(u.date || u.created_at);
              return (
                <Reveal key={u.id} delay={(i % 3) * 70}>
                  <article data-testid={`update-full-${u.id}`}
                    className={`grid sm:grid-cols-[110px_1fr] gap-6 rounded-2xl border p-6 sm:p-8 shadow-card ${
                      u.is_new ? "border-brandgreen/40 bg-brandgreen/[0.04]" : "border-line bg-white"
                    }`}>
                    <div className="flex sm:flex-col items-center sm:items-start gap-2">
                      <div className="rounded-xl bg-navy-deep text-white text-center px-4 py-3 leading-tight" dir="ltr">
                        <div className="font-display text-2xl text-gold">{dp.d}</div>
                        <div className="text-[0.65rem] tracking-wider text-white/60">{dp.m}</div>
                        {dp.y && <div className="text-[0.65rem] text-white/40">{dp.y}</div>}
                      </div>
                      {u.is_new && <span className="text-[0.6rem] font-bold tracking-wider uppercase bg-brandgreen text-white px-2.5 py-1 rounded-full">{t({ en: "New", ar: "جديد" })}</span>}
                    </div>
                    <div>
                      {u.category && <div className="text-[0.7rem] uppercase tracking-wider text-crimson-600 font-semibold mb-1 flex items-center gap-2">{u.category}{u.official && <span className="inline-flex items-center gap-1 text-brandgreen bg-brandgreen/10 px-2 py-0.5 rounded-full normal-case tracking-normal"><ShieldCheck className="h-3 w-3" />{t({ en: "Official source", ar: "مصدر رسمي" })}</span>}</div>}
                      <h2 className="font-display text-xl sm:text-2xl text-ink leading-snug">{isAr ? (u.title_ar || u.title_en) : (u.title_en || u.title_ar)}</h2>
                      {(u.summary_en || u.summary_ar) && <p className="mt-3 text-muted leading-relaxed">{isAr ? (u.summary_ar || u.summary_en) : (u.summary_en || u.summary_ar)}</p>}
                      {u.source_url ? (
                        <a href={u.source_url} target="_blank" rel="noopener noreferrer" data-testid={`update-source-${u.id}`}
                          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brandgreen hover:text-brandgreen-600 transition-colors">
                          <ExternalLink className="h-4 w-4" /> {t({ en: "Read the official source", ar: "اقرأ المصدر الرسمي" })}
                        </a>
                      ) : (
                        <div className="mt-4 text-xs text-muted italic">{t({ en: "Summarised update — verify with the Federal Tax Authority.", ar: "تحديث مُلخّص — يُرجى التحقق من الهيئة الاتحادية للضرائب." })}</div>
                      )}
                    </div>
                  </article>
                </Reveal>
              );
            }) : TAX_UPDATES.map((u, i) => (
              <Reveal key={u.id} delay={(i % 3) * 70}>
                <article data-testid={`update-full-${u.id}`}
                  className={`grid sm:grid-cols-[110px_1fr] gap-6 rounded-2xl border p-6 sm:p-8 shadow-card ${
                    u.tag === "NEW" ? "border-brandgreen/40 bg-brandgreen/[0.04]" : "border-line bg-white"
                  }`}>
                  <div className="flex sm:flex-col items-center sm:items-start gap-2">
                    <div className="rounded-xl bg-navy-deep text-white text-center px-4 py-3 leading-tight" dir="ltr">
                      <div className="font-display text-2xl text-gold">{u.date.d}</div>
                      <div className="text-[0.65rem] tracking-wider text-white/60">{t(u.date.m)}</div>
                      {u.date.y && <div className="text-[0.65rem] text-white/40">{u.date.y}</div>}
                    </div>
                    {u.tag === "NEW" && <span className="text-[0.6rem] font-bold tracking-wider uppercase bg-brandgreen text-white px-2.5 py-1 rounded-full">{t({ en: "New", ar: "جديد" })}</span>}
                  </div>
                  <div>
                    <h2 className="font-display text-xl sm:text-2xl text-ink leading-snug">{t(u.title)}</h2>
                    <p className="mt-3 text-muted leading-relaxed">{t(u.body)}</p>
                    {u.fact && <div className="mt-4 text-sm font-medium text-navy bg-surface rounded-xl px-4 py-3 leading-snug">{t(u.fact)}</div>}
                    <a href={u.source.url} target="_blank" rel="noopener noreferrer" data-testid={`update-source-${u.id}`}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brandgreen hover:text-brandgreen-600 transition-colors">
                      <ExternalLink className="h-4 w-4" /> {t(u.source.label)}
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="relative overflow-hidden mt-14 rounded-[1.75rem] bg-navy-deep p-8 md:p-10 text-center">
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMAGES.brandTexture})`, backgroundSize: "cover" }} />
              <h3 className="relative font-display text-2xl md:text-3xl text-white">{t({ en: "Need help applying these changes to your business?", ar: "تحتاج مساعدة في تطبيق هذه التغييرات على أعمالك؟" })}</h3>
              <Link to="/#contact" data-testid="updates-cta-contact"
                className="relative mt-6 inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full bg-gold text-navy-deep font-semibold hover:bg-white transition-colors">
                {t({ en: "Talk to Smart Account Vision", ar: "تحدّث مع سمارت أكاونت فيجن" })} <Arrow className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
